import React, { useEffect, useState, useRef } from 'react';
import mapboxgl from 'maplibre-gl';
import config from 'config';

import * as d3 from 'd3';

import CONSTANTS from 'utils/constants';

import { createIcons } from './utils';
import './index.scss';

const colors = [
  // temporary purple rgba(190,93,233,0.7)
  'rgba(62, 142, 208, 0.7)', // blue - CREATED
  'rgba(167,167,167,0.7)', // grey - MAINTENANCE
  'rgba(252,40,44, 0.7)', // red - INSTALLED/OPERATIONAL(With problem)
  'rgba(252,169,17, 0.7)', // orange - INSTALLED/OPERATIONAL(Intermediary)
  'rgba(135,197,3,0.7)', // green - INSTALLED/OPERATIONAL(OK)
];

const colorsDomains = [
  'colorIsBlue',
  'colorIsGrey',
  'colorIsRed',
  'colorIsOrange',
  'colorIsGreen',
];

// using d3 to create a consistent color scale
const colorScale = d3.scaleOrdinal().domain(colorsDomains).range(colors);

const createDonutChart = (props) => {
  // const createDonutChart = (props, totals) => {
  // create a div element to hold our marker
  const div = document.createElement('div');

  // create our array with our data
  const data = [
    { type: 'colorIsBlue', count: props.colorIsBlue },
    { type: 'colorIsGrey', count: props.colorIsGrey },
    { type: 'colorIsRed', count: props.colorIsRed },
    { type: 'colorIsOrange', count: props.colorIsOrange },
    { type: 'colorIsGreen', count: props.colorIsGreen },
  ];

  const maxProps = { index: null, count: 0, name: null };
  colorsDomains.forEach((prop, index) => {
    if (props[prop] > maxProps.count) {
      maxProps.index = index;
      maxProps.count = props[prop];
      maxProps.name = prop;
    }
  });

  // svg config
  const thickness = 15;

  // // this sets the scale for our circle radius and this is why we need the totals. We need to set a mininum and a maximum to define the domain and the range.
  // const scale = d3
  //   .scaleLinear()
  //   .domain([d3.min(totals), d3.max(totals)])
  //   .range([1500, d3.max(totals)]);

  // calculate the radius
  // const radius = Math.sqrt(scale(props.point_count));
  const radius = 30;
  // calculate the radius of the smaller circle
  const circleRadius = radius - thickness;

  // create the svg
  const svg = d3
    .select(div)
    .append('svg')
    .attr('class', 'pie')
    .attr('width', radius * 2)
    .attr('height', radius * 2);

  // create a group to hold our arc paths and center
  const g = svg
    .append('g')
    .attr('transform', `translate(${radius}, ${radius})`);

  // create an arc using the radius above
  const arc = d3.arc().innerRadius(circleRadius).outerRadius(radius);

  // create the pie for the donut
  const pie = d3
    .pie()
    .value((d) => d.count)
    .sort(null);

  // using the pie and the arc, create our path based on the data
  g.selectAll('path')
    .data(pie(data.sort((x, y) => d3.ascending(y.count, x.count))))
    .enter()
    .append('path')
    .attr('d', arc)
    .attr('fill', (d) => colorScale(d.data.type));

  // create the center circle
  g.append('circle')
    .attr('r', circleRadius)
    .attr('fill', colors[maxProps.index])
    .attr('class', 'center-circle');

  // create the text
  g.append('text')
    .attr('class', 'total')
    .text(props.point_count_abbreviated)
    .attr('text-anchor', 'middle')
    .attr('dy', 5)
    .attr('fill', 'white');

  return div;
};

export default ({
  center = null,
  currentPosition = {},
  markers = [],
  showLines = true,
  options = { zoom: null },
  style = CONSTANTS.MAPBOX.STYLES.STREETS,
  onMapMoved,
}) => {
  const defaultCenter = { longitude: 1.44, latitude: 43.6 };
  const defaultZoom = 2;
  const defaultStyle = CONSTANTS.MAPBOX.STYLES.STREETS;

  const [map, setMap] = useState();
  const [isInitializingMap, setIsInitializingMap] = useState(false);
  const [icons] = useState(createIcons());
  const [geoJSONMarkers, setGeoJSONMarkers] = useState({
    type: 'FeatureCollection',
    features: [],
  });
  // const [currentPosition, setCurrentPosition] = useState(null);
  const [currentPositionMarker, setCurrentPositionMarker] = useState(null);
  const [clustersMarkers, setClustersMarkers] = useState({
    markers: {},
    new: {},
    onScreen: {},
  });

  const mapContainer = useRef(null);

  const {
    marker_0_50, // eslint-disable-line camelcase
    marker_0_100, // eslint-disable-line camelcase
    marker_50_50, // eslint-disable-line camelcase
    marker_50_100, // eslint-disable-line camelcase
    marker_100_50, // eslint-disable-line camelcase
    marker_100_100, // eslint-disable-line camelcase
    marker_CREATED_50, // eslint-disable-line camelcase
    marker_CREATED_100, // eslint-disable-line camelcase
    marker_MAINTENANCE_50, // eslint-disable-line camelcase
    marker_MAINTENANCE_100, // eslint-disable-line camelcase
    imageLineElement,
  } = icons;

  const drawLayers = (_map) => {
    const map = _map;
    const source = map.getSource('devices');

    if (!source) {
      map.resize();

      const signalIs0 = ['==', ['get', 'signal'], 0];
      const signalIs1 = ['==', ['get', 'signal'], 1];
      const signalIs2 = ['==', ['get', 'signal'], 2];

      const statusIsCREATED = [
        '==',
        ['get', 'status'],
        CONSTANTS.STATIONS_STATUS_ENUM.CREATED,
      ];

      const statusIsNotCREATED = [
        '!=',
        ['get', 'status'],
        CONSTANTS.STATIONS_STATUS_ENUM.CREATED,
      ];
      const statusIsMAINTENANCE = [
        '==',
        ['get', 'status'],
        CONSTANTS.STATIONS_STATUS_ENUM.MAINTENANCE,
      ];

      const statusIsNotMAINTENANCE = [
        '!=',
        ['get', 'status'],
        CONSTANTS.STATIONS_STATUS_ENUM.MAINTENANCE,
      ];

      const colorIsBlue = statusIsCREATED;
      const colorIsGrey = statusIsMAINTENANCE;
      const colorIsGreen = [
        'all',
        statusIsNotCREATED,
        statusIsNotMAINTENANCE,
        signalIs2,
      ];
      const colorIsOrange = [
        'all',
        statusIsNotCREATED,
        statusIsNotMAINTENANCE,
        signalIs1,
      ];
      const colorIsRed = [
        'all',
        statusIsNotCREATED,
        statusIsNotMAINTENANCE,
        signalIs0,
      ];

      map.addSource('devices', {
        type: 'geojson',
        data: geoJSONMarkers,
        cluster: true,
        clusterRadius: 30,
        clusterProperties: {
          colorIsRed: ['+', ['case', colorIsRed, 1, 0]],
          colorIsOrange: ['+', ['case', colorIsOrange, 1, 0]],
          colorIsGreen: ['+', ['case', colorIsGreen, 1, 0]],
          colorIsBlue: ['+', ['case', colorIsBlue, 1, 0]],
          colorIsGrey: ['+', ['case', colorIsGrey, 1, 0]],
        },
      });
      const iconsOpacity = 0.7;

      // map.addLayer({
      //   id: 'clusters',
      //   type: 'circle',
      //   source: 'devices',
      //   filter: ['all', ['has', 'point_count']],
      //   paint: {
      //     'circle-color': [
      //       'case',
      //       ['>', ['get', 'signalIs0'], 0],
      //       '#fc282c', // red
      //       ['>', ['get', 'signalIs1'], 0],
      //       '#fca911', // orange
      //       ['>', ['get', 'signalIs2'], 0],
      //       '#87c503', // green
      //       '#51bbd6',
      //     ],
      //     'circle-opacity': iconsOpacity,
      //     'circle-radius': 25,
      //   },
      // });

      // map.addLayer({
      //   id: 'cluster-count',
      //   type: 'symbol',
      //   source: 'devices',
      //   filter: ['has', 'point_count'],
      //   layout: {
      //     'text-field': '{point_count_abbreviated}',
      //     'text-size': 12,
      //   },
      // });

      map.addImage('station-line-icon', imageLineElement);
      map.addImage('marker_signal-0_battery-50', marker_0_50);
      map.addImage('marker_signal-0_battery-100', marker_0_100);
      map.addImage('marker_signal-50_battery-50', marker_50_50);
      map.addImage('marker_signal-50_battery-100', marker_50_100);
      map.addImage('marker_signal-100_battery-50', marker_100_50);
      map.addImage('marker_signal-100_battery-100', marker_100_100);
      map.addImage('marker_CREATED_battery-50', marker_CREATED_50);
      map.addImage('marker_CREATED_battery-100', marker_CREATED_100);
      map.addImage('marker_MAINTENANCE_battery-50', marker_MAINTENANCE_50);
      map.addImage('marker_MAINTENANCE_battery-100', marker_MAINTENANCE_100);

      map.addLayer({
        id: 'unclustered-point-icon-line',
        type: 'symbol',
        source: 'devices',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'station-line-icon',
          'icon-rotate': ['number', ['get', 'rotation'], 0],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
      });

      map.addLayer({
        id: 'unclustered-point-name',
        type: 'symbol',
        source: 'devices',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'text-allow-overlap': true,
          'text-anchor': 'top',
          'text-field': ['get', 'serialNumber'],
          'text-offset': [0, 1],
          'text-size': 12,
        },
        paint: { 'text-color': '#616161' },
      });

      const batteryLow = ['==', ['get', 'battery'], 0];
      const batteryFull = ['==', ['get', 'battery'], 1];
      const signalLow = ['==', ['get', 'signal'], 0];
      const signalMedium = ['==', ['get', 'signal'], 1];
      const signalHigh = ['==', ['get', 'signal'], 2];

      map.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'devices',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': [
            'case',
            ['all', statusIsCREATED, batteryLow],
            'marker_CREATED_battery-50',
            ['all', statusIsCREATED, batteryFull],
            'marker_CREATED_battery-100',
            ['all', statusIsMAINTENANCE, batteryLow],
            'marker_MAINTENANCE_battery-50',
            ['all', statusIsMAINTENANCE, batteryFull],
            'marker_MAINTENANCE_battery-100',
            ['all', signalLow, batteryLow],
            'marker_signal-0_battery-50',
            ['all', signalLow, batteryFull],
            'marker_signal-0_battery-100',
            ['all', signalMedium, batteryLow],
            'marker_signal-50_battery-50',
            ['all', signalMedium, batteryFull],
            'marker_signal-50_battery-100',
            ['all', signalHigh, batteryLow],
            'marker_signal-100_battery-50',
            ['all', signalHigh, batteryFull],
            'marker_signal-100_battery-100',
            'marker_signal-0_battery-50',
          ],
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true,
        },
        paint: {
          'icon-opacity': iconsOpacity,
        },
      });

      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });

        const clusterId = features[0].properties.cluster_id;
        map
          .getSource('devices')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;
            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom,
            });
          });
      });

      map.on('click', 'unclustered-point', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const { html } = e.features[0].properties;

        new mapboxgl.Popup({ className: 'popup-marker', offset: 25 })
          .setHTML(html)
          .setLngLat(coordinates)
          .addTo(map);
      });

      map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
      });

      map.on('mouseenter', 'unclustered-point', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', 'unclustered-point', () => {
        map.getCanvas().style.cursor = '';
      });

      map.on('moveend', () => {
        if (onMapMoved) {
          const mapCenter = map.getCenter();
          const center = {
            latitude: mapCenter.lat,
            longitude: mapCenter.lng,
          };
          onMapMoved({
            center,
            zoom: map.getZoom(),
          });
        }
      });
    }

    return map;
  };

  const updateMarkers = (rawMarkers) => {
    const geoJSONCollection = { type: 'FeatureCollection', features: [] };

    // Add markers
    geoJSONCollection.features = rawMarkers.map((marker = {}) => {
      // FIXME
      const {
        id,
        name,
        serialNumber,
        location,
        rotation,
        html = '',
        signalStatus,
        batteryStatus,
        status,
      } = marker;
      if (!location) {
        return false;
      }
      const { latitude, longitude } = location;
      if (!latitude || !longitude) {
        return false;
      }

      let battery = 0;
      if (batteryStatus === CONSTANTS.BATTERY_STATUS.OK) {
        battery = 1;
      }
      let signal = 0;
      if (signalStatus === CONSTANTS.SIGNALS_STATUS.LOW) {
        signal = 1;
      } else if (signalStatus === CONSTANTS.SIGNALS_STATUS.OK) {
        signal = 2;
      }
      const offset = [Math.random() / 1000, Math.random() / 1000];
      const geoJSONPoint = {
        type: 'Feature',
        properties: {
          id,
          name,
          serialNumber,
          rotation,
          battery,
          signal,
          html,
          status,
        },
        geometry: {
          type: 'Point',
          coordinates: [longitude + offset[0], latitude + offset[1]],
        },
      };
      return geoJSONPoint;
    });
    setGeoJSONMarkers(geoJSONCollection);

    if (map) {
      map.on('load', () => {
        drawLayers(map);
        const source = map.getSource('devices');
        if (source) {
          // Update markers in source
          source.setData(geoJSONCollection);
        }
      });
      const getPointCount = (features) => {
        const pointCounts = [];
        features.forEach((f) => {
          if (f.properties.cluster) {
            // console.log('properties', f.properties);
            pointCounts.push(f.properties.point_count);
          }
        });
        return pointCounts;
      };

      const updateClustersMarkers = () => {
        const newMarkers = {};
        let totals = null;
        const features = map.querySourceFeatures('devices');

        totals = getPointCount(features);
        features.forEach((feature) => {
          const { coordinates } = feature.geometry;
          // get our properties, which include our clustered properties
          const props = feature.properties;
          // continue only if the point is part of a cluster
          if (!props.cluster) {
            return;
          }
          // if yes, get the cluster_id
          const id = props.cluster_id;
          // create a marker object with the cluster_id as a key
          let clusterMarker = clustersMarkers.markers[id];
          // if that marker doesn't exist yet, create it
          if (!clusterMarker) {
            // create an html element (more on this later)
            const el = createDonutChart(props, totals);
            // create the marker object passing the html element and the coordinates
            clusterMarker = new mapboxgl.Marker({
              element: el,
            }).setLngLat(coordinates);
            clustersMarkers.markers[id] = clusterMarker;
          }
          // create an object in our newMarkers object with our current marker representing the current cluster
          newMarkers[id] = clusterMarker;

          // if the marker isn't already on screen then add it to the map
          if (!clustersMarkers.onScreen[id]) {
            clusterMarker.addTo(map);
          }
        });

        Object.keys(clustersMarkers.onScreen).forEach((id) => {
          // if there isn't a new marker with that id, then it's not visible, therefore remove it.
          if (!newMarkers[id]) {
            clustersMarkers.onScreen[id].remove();
          }
        });
        // otherwise, it is visible and we need to add it to our markersOnScreen object
        clustersMarkers.onScreen = newMarkers;
        setClustersMarkers(clustersMarkers);
      };

      map.on('data', (e) => {
        if (e.sourceId !== 'devices' || !e.isSourceLoaded) return;

        map.on('move', updateClustersMarkers);
        map.on('moveend', updateClustersMarkers);
        updateClustersMarkers();
      });
    }

    return geoJSONCollection;
  };

  // Default hook
  useEffect(() => {
    if (!map) {
      // initializeMap({ setMap, mapContainer });
      if (!isInitializingMap) {
        setIsInitializingMap(true);
        const latitude = (center && center.latitude) || defaultCenter.latitude;
        const longitude =
          (center && center.longitude) || defaultCenter.longitude;
        const zoom = (options && options.zoom) || defaultZoom;
        const newStyle = style || defaultStyle;

        const mapStyle = `${CONSTANTS.MAPBOX.STYLE_URL}${newStyle}`;

        mapboxgl.accessToken = config.mapbox.accessToken;
        const currentMap = new mapboxgl.Map({
          container: mapContainer.current,
          style: mapStyle,
          center: [longitude, latitude],
          zoom,
        });

        setMap(currentMap);
        setIsInitializingMap(false);
      }
    }

    return () => {
      if (map) {
        map.remove();
      }
    };
  }, [map, isInitializingMap]);

  useEffect(() => {
    const geoMarkers = updateMarkers(markers);

    if (map && !isInitializingMap) {
      const source = map.getSource('devices');

      if (source) {
        // Update markers in source
        source.setData(geoMarkers);
      }
    }
  }, [markers]);

  useEffect(() => {
    if (map && currentPosition) {
      let positionMarker = currentPositionMarker;
      if (!currentPositionMarker) {
        positionMarker = new mapboxgl.Marker();
      }
      if (currentPosition.coords) {
        positionMarker.setLngLat([
          currentPosition.coords.longitude,
          currentPosition.coords.latitude,
        ]);
        setCurrentPositionMarker(positionMarker);
        positionMarker.addTo(map);
      }
    } else if (currentPositionMarker && !currentPosition) {
      currentPositionMarker.remove();
    }
  }, [map, currentPosition]);

  // Update lines of sight
  useEffect(() => {
    if (map) {
      if (showLines) {
        map.setLayoutProperty(
          'unclustered-point-icon-line',
          'visibility',
          'visible'
        );
      } else {
        map.setLayoutProperty(
          'unclustered-point-icon-line',
          'visibility',
          'none'
        );
      }
    }
  }, [showLines]);

  // Update map'style
  useEffect(() => {
    if (map) {
      const newStyle = `${CONSTANTS.MAPBOX.STYLE_URL}${style}`;
      map.setStyle(newStyle);
      // When style is loaded, idle is triggered
      // Then drawLayers to new style
      map.on('idle', () => {
        drawLayers(map);
      });
    }
  }, [style]);

  return (
    <div
      className="map-container is-flex-grow-1"
      ref={(el) => {
        mapContainer.current = el;
      }}
    />
  );
};
